import React, { useState, useCallback, useRef, Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import SelectInput from "@components/inputs/SelectInput/SelectInput";
import Button from "@components/buttons/Button/Button";
import styles from "@styles/CommonAuthPageLayout.module.css";
import { isRequiredField, isValidEmail, isValidMaxLength, isValidPassword } from "@util/validators";
import { signUp } from "@services/userService";
import { getRootDomain, getRegion, REGION_OPTIONS } from "@util/domain";
import { MAX_NAME_LENGTH } from "@constants/validation";
import ReCAPTCHA, { ReCAPTCHAInterface } from "@components/misc/ReCAPTCHA/ReCAPTCHA";

const INVALID_CREDENTIALS_MESSAGE = "Unable to sign up with these credentials. Please try again.";

const SignUpPage = () => {
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    // Defaults to the hub the visitor is on; changeable for people signing up while travelling or on a VPN
    const [region, setRegion] = useState<string>(getRegion());

    const [nameError, setNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [signUpError, setSignUpError] = useState("");
    const [loading, setLoading] = useState(false);

    const recaptchaRef = useRef<ReCAPTCHAInterface>(null);
    const [captchaError, setCaptchaError] = useState<string | null>(null);

    const validateName = useCallback((val: string) => {
        const result = isRequiredField(val, "Name");
        if (result) {
            return result;
        }
        return isValidMaxLength(val, MAX_NAME_LENGTH);
    }, []);

    const validateEmail = useCallback((val: string) => {
        return isValidEmail(val);
    }, []);

    const validatePassword = useCallback((val: string) => {
        return isValidPassword(val);
    }, []);

    const validateConfirmPassword = useCallback((val: string) => {
        if (val !== password) {
            return "Passwords do not match.";
        }
        return "";
    }, [password]);

    const onNameChange = useCallback((val: string) => {
        setName(val);
        setNameError(validateName(val));
    }, [validateName]);

    const onEmailChange = useCallback((val: string) => {
        setEmail(val);
        setEmailError(validateEmail(val));
    }, [validateEmail]);

    const onPasswordChange = useCallback((val: string) => {
        setPassword(val);
        setPasswordError(validatePassword(val));
    }, [validatePassword]);

    const onConfirmPasswordChange = useCallback((val: string) => {
        setConfirmPassword(val);
        setConfirmPasswordError(validateConfirmPassword(val));
    }, [validateConfirmPassword]);

    const onPasswordBlur = useCallback(() => {
        setPasswordError(validatePassword(password));
        setConfirmPasswordError(validateConfirmPassword(confirmPassword));
    }, [confirmPassword, password, validateConfirmPassword, validatePassword]);

    const onConfirmPasswordBlur = useCallback(() => {
        setConfirmPasswordError(validateConfirmPassword(confirmPassword));
    }, [confirmPassword, validateConfirmPassword]);

    // Sign-Up Handler with specific error messages
    const handleSignUp = async () => {
        let isValid = true;

        // Validate Name
        const nameValidationError = validateName(name);
        setNameError(nameValidationError);
        if (nameValidationError) isValid = false;

        // Validate Email
        const emailValidationError = validateEmail(email);
        setEmailError(emailValidationError);
        if (emailValidationError) isValid = false;

        // Validate Password
        const passwordValidationError = validatePassword(password);
        setPasswordError(passwordValidationError);
        if (passwordValidationError) isValid = false;

        const passwordConfirmValidationError = validateConfirmPassword(confirmPassword);
        setConfirmPasswordError(passwordConfirmValidationError);
        if (passwordConfirmValidationError) isValid = false;

        const captchaToken = await recaptchaRef.current?.execute();
        if (!captchaToken) {
            setCaptchaError("reCAPTCHA verification failed.");
            return;
        }

        // If both email and password are valid, proceed
        if (isValid) {
            setSignUpError("");
            setCaptchaError("");
            setLoading(true); // Show loading

            try {
                const [firstName, lastName = ""] = name.trim().split(" ");
                await signUp(email, password, firstName, lastName, captchaToken, region === "global" ? "global" : "au");
                navigate("/auth/sign-up-success");
            } catch (error) {
                setSignUpError(INVALID_CREDENTIALS_MESSAGE);
            } finally {
                setLoading(false); // Hide loading
            }
        } else {
            // Show the appropriate error message
            setSignUpError(INVALID_CREDENTIALS_MESSAGE);
        }
    };

    return (
        <>
            <Helmet>
                <title>Create Account | Memorial Hub | Forever In Our Hearts</title>
                <meta
                    name="description"
                    content={`Join the Memorial Hub to manage your online memorials and tributes. Easily update, ${getRegion() === "global" ? "personalize" : "personalise"}, and share your loved one’s legacy pages with family and friends.`}
                />
            </Helmet>
            <div>
                <Card className={styles.cardContainer}>
                    <TextInput
                        label="Full Name"
                        id="name"
                        value={name}
                        onChange={onNameChange}
                        placeholder="Enter your name..."
                        errorMessage={nameError}
                        disabled={loading} // Disable while loading
                    />
                    <TextInput
                        label="Email"
                        id="email"
                        value={email}
                        onChange={onEmailChange}
                        placeholder="Enter email..."
                        errorMessage={emailError}
                        disabled={loading} // Disable while loading
                    />
                    <TextInput
                        label="Password"
                        id="password"
                        value={password}
                        onChange={onPasswordChange}
                        onBlur={onPasswordBlur}
                        placeholder="Enter password..."
                        type="password"
                        errorMessage={passwordError}
                        disabled={loading} // Disable while loading
                    />
                    <TextInput
                        label="Confirm Password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={onConfirmPasswordChange}
                        onBlur={onConfirmPasswordBlur}
                        placeholder="Confirm password..."
                        type="password"
                        errorMessage={confirmPasswordError}
                        disabled={loading} // Disable while loading
                    />
                    <SelectInput
                        label="Where are you based?"
                        id="region"
                        value={region}
                        onChange={setRegion}
                        options={REGION_OPTIONS}
                        placeholder="Select your region..."
                        disabled={loading}
                    />
                    <Button
                        variant="primary"
                        onClick={handleSignUp}
                        disabled={loading} // Disable button during loading
                    >
                        {loading ? "Signing Up..." : "Create Account"}
                        {" "}
                        {/* Show loading text */}
                    </Button>

                    <p className={styles.terms}>
                        By signing up, you agree to the
                        <br />
                        <a
                            href={`${getRootDomain()}/terms-and-conditions`}
                            className={styles.link}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Terms of Use
                        </a>
                    </p>
                    {signUpError && (<p className={styles.errorMessage}>{signUpError}</p>)}
                    {captchaError && <div className={styles.errorMessage}>{captchaError}</div>}
                </Card>

                <div className={styles.linkContainer}>
                    <p>Already have an account?</p>
                    <div>
                        <Link to="/auth/sign-in" className={styles.link}>
                            Sign In
                        </Link>
                    </div>
                </div>
                <ReCAPTCHA ref={recaptchaRef} />
            </div>
        </>
    );
};

export default SignUpPage;
